import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { AuthActions } from './auth.actions';
import { AuthService } from '../../core/services/auth/auth.service';
import { UserAuthActions } from '../user/auth/user-auth.actions';
import { ToastService } from '../../core/services/toast.service';
import { APP_PATHS, firstAllowedAppRoute } from '../../core/constants/app-config.constants';

@Injectable()
export class AuthEffects {
  private actions$     = inject(Actions);
  private authService  = inject(AuthService);
  private router       = inject(Router);
  private store        = inject(Store);
  private toastService = inject(ToastService);

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      mergeMap(({ username, password }) =>
        this.authService.login({ username, password }).pipe(
          map(response => AuthActions.loginSuccess({
            accessToken:  response.body?.data?.[0]?.accessToken  ?? '',
            refreshToken: response.body?.data?.[0]?.refreshToken ?? '',
          })),
          catchError(error =>
            of(AuthActions.loginFailure({
              error: error?.error?.message || error?.message || 'Login failed',
            }))
          )
        )
      )
    )
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(() => this.store.dispatch(UserAuthActions.loadUserDetails())),
        switchMap(() =>
          this.actions$.pipe(
            ofType(
              UserAuthActions.loadUserDetailsSuccess,
              UserAuthActions.loadUserDetailsFailure,
              UserAuthActions.accountDeactivated,
            ),
            take(1),
            tap(resultAction => {
              if (resultAction.type === UserAuthActions.loadUserDetailsSuccess.type) {
                const privileges = resultAction.response.data?.[0]?.privileges ?? [];
                this.toastService.success('Login Successful', 'Welcome back!');
                void this.router.navigateByUrl(firstAllowedAppRoute(privileges));
              } else if (resultAction.type === UserAuthActions.loadUserDetailsFailure.type) {
                const failureAction = resultAction as { error?: string };
                this.toastService.error(
                  'Access Denied',
                  failureAction.error || 'Unable to load user profile.'
                );
                this.authService.clearToken();
              }              
            })
          )
        )
      ),
    { dispatch: false }
  );

  loginFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginFailure),
        tap(() => this.toastService.error('Login Failed', 'Invalid username or password.'))
      ),
    { dispatch: false }
  );

  // Fires on INACTIVE status from any loadUserDetails call — login OR active session
  accountDeactivated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAuthActions.accountDeactivated),
      tap(({ message }) =>
        this.toastService.error('Access Denied', message)
      ),
      map(() => AuthActions.logout())
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      map(() => {
        this.authService.clearToken();
        this.store.dispatch(UserAuthActions.clearUser());
        return AuthActions.logoutSuccess();
      })
    )
  );

  logoutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSuccess),
        tap(() => this.router.navigate([`/${APP_PATHS.LOGIN}`]))
      ),
    { dispatch: false }
  );
}
